<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import TabSwitch from '@/components/shared/TabSwitch.vue';
import { useInstitutionStore } from '@/stores/institution';
import { useDepartmentStore } from '@/stores/department';
import { useProfilesStore } from '@/stores/profiles';
import { default as ButtonEl } from '@/components/shared/Button.vue';
import i18n from '@/utils/i18n';
import DepartmentSelectorContent from '@/components/shared/DepartmentSelectorContent.vue';
import { ITab } from '@/components/shared/TabSwitch.vue';
import { IDepartment, IProfile } from '@/interfaces';
import RoundProfile from '@/components/shared/RoundProfile.vue';
import SquareProfile from '@/components/shared/SquareProfile.vue';
import DepartmentSelectorList from '@/components/shared/DepartmentSelectorList.vue';
import {
  DepartmentFragment,
  InstitutionDepartment,
  MealParticipantFragment,
} from '@/graphql/custom-types';

const institutionStore = useInstitutionStore();
const departmentStore = useDepartmentStore();
const profilesStore = useProfilesStore();

const emit = defineEmits([
  'profile-selected',
  'profiles-selected',
  'save',
  'department-selected',
  'close',
  'confirm',
]);

interface Props {
  participants?: (IProfile | MealParticipantFragment)[];
  profiles?: (IProfile | MealParticipantFragment)[];
  participating?: boolean;
  canSave?: boolean;
  selectedProfileType?: 'RESIDENT' | 'EMPLOYEE';
  multiselect?: boolean;
  employeeOnly?: boolean;
  selectBottom?: string;
  participantsRequired?: boolean;
  squareTiles?: boolean;
  confirmButtonText?: string;
  sharedDepartments: (IDepartment | DepartmentFragment)[];
}

const props = withDefaults(defineProps<Props>(), {
  participants: () => [],
  profiles: () => [],
  participating: true,
  selectedProfileType: 'RESIDENT',
  canSave: false,
  multiselect: false,
  employeeOnly: false,
  selectBottom: 'bottom-12',
  participantsRequired: true,
});

const selectedProfiles = ref<IProfile[]>([]);
const selectedDepartment = ref<InstitutionDepartment>();
const showDepartmentsList = ref(false);

const PROFILE_TYPES: ITab[] = [
  {
    title: i18n.global.t('ourHome.residents'),
    value: 'RESIDENT',
    icon: ['fas', 'fa-user'],
  },
  {
    title: i18n.global.t('ourHome.employees'),
    value: 'EMPLOYEE',
    icon: ['fas', 'fa-id-card'],
  },
];

const availableDepartments = computed((): IDepartment[] => {
  return props.sharedDepartments.reduce((accumulator, current) => {
    (institutionStore.departments || []).forEach((department) => {
      if (department.id === current.id) {
        accumulator.push(current as never);
      }
    });
    return accumulator;
  }, []);
});

const profileType = ref<ITab>(PROFILE_TYPES[0]);

const shownProfiles = computed((): IProfile[] => {
  const filteredProfiles = profilesStore.all
    .filter((profile: IProfile) =>
      getDepartmentIds(profile).includes(
        parseInt(selectedDepartment?.value?.id || '-1')
      )
    )
    .filter((profile: IProfile) => profile.type === profileType.value.value)
    .sort((a: IProfile, b: IProfile) =>
      a.displayName.localeCompare(
        b.displayName,
        institutionStore.locale || 'da'
      )
    );

  if (!props.participants.length) {
    return filteredProfiles;
  }

  if (props.participating === false) {
    return filteredProfiles.filter((profile: IProfile) =>
      props.participants.some((participant) => participant.id === profile.id)
    );
  }

  return filteredProfiles.filter(
    (profile: IProfile) =>
      !props.participants.some((participant) => participant.id === profile.id)
  );
});

const size = computed(() => {
  return shownProfiles.value.length > 25 ? 'small' : 'large';
});

onMounted(() => {
  // TODO: can be updated to just use selectedProfileType prop when files using this component is migrated to TS
  const selectedType = PROFILE_TYPES.find(
    (type) => type.value === props.selectedProfileType
  );

  profileType.value = props.employeeOnly
    ? PROFILE_TYPES[1]
    : selectedType || PROFILE_TYPES[0];
  selectedDepartment.value = (institutionStore.departments || []).find(
    (department) => department.id == departmentStore.id
  );
});

function getDepartmentIds(profile: IProfile) {
  return profile.departments.map((department) => parseInt(department.id));
}

function selectProfile(profile: IProfile) {
  const profileIndex = selectedProfiles.value.indexOf(profile);

  if (profileIndex > -1) {
    selectedProfiles.value.splice(profileIndex, 1);
  } else {
    selectedProfiles.value.push(profile);
  }

  if (!props.multiselect) {
    emit('profile-selected', profile);
  } else {
    emit('profiles-selected', selectedProfiles);
  }
}

function isSelected(profile: IProfile) {
  return selectedProfiles.value.indexOf(profile) > -1;
}

const departmentSelected = (department: IDepartment) => {
  selectedDepartment.value = department;
  showDepartmentsList.value = false;
  emit('department-selected', department);
};
</script>

<template>
  <div class="flex flex-col mb-12 relative">
    <div class="flex flex-col h-full relative">
      <TabSwitch
        v-if="!employeeOnly"
        @tab-selected="profileType = $event"
        :items="PROFILE_TYPES"
        :selectedItem="profileType"
      />

      <div
        class="grid grid-cols-3 pt-12 max-h-full gap-10"
        :class="{
          'grid-cols-3 gap-x-8': size === 'large',
          'grid-cols-5 gap-x-3': size !== 'large',
        }"
      >
        <RoundProfile
          v-if="!squareTiles"
          v-for="(profile, index) in shownProfiles"
          :key="index"
          :profile="profile"
          :size="size"
          :is-selected="isSelected(profile)"
          @select-profile="selectProfile"
        />

        <SquareProfile
          v-if="squareTiles"
          v-for="(profile, index) in shownProfiles"
          :key="index"
          :profile="profile"
          :size="size"
          :is-selected="isSelected(profile)"
          @select-profile="selectProfile"
        />
      </div>

      <div
        class="sticky w-91.75 left-1/2 transform -translate-x-1/2 bottom-18.75 flex flex-col items-center z-20"
      >
        <div
          class="z-75 w-full"
          :class="
            multiselect && confirmButtonText ? 'bottom-33.75' : 'bottom-12.5'
          "
        >
          <DepartmentSelectorContent
            v-if="selectedDepartment"
            :selected-department="selectedDepartment"
            @department-selected="departmentSelected"
            @click="showDepartmentsList = !showDepartmentsList"
          />

          <DepartmentSelectorList
            class="absolute"
            v-if="showDepartmentsList"
            :bottom-margin="
              multiselect && confirmButtonText
                ? 'bottom-[195px]'
                : 'bottom-[124px]'
            "
            :selected-department="selectedDepartment"
            :departments="availableDepartments"
            @department-selected="departmentSelected"
          />
        </div>

        <ButtonEl
          v-if="multiselect && confirmButtonText"
          :text="confirmButtonText"
          background="bg-green-400"
          rounded="full"
          :icon="['far', 'fa-check']"
          icon-size="text-2xl"
          typography="h700"
          class="w-fit"
          :style="{ bottom: '40px' }"
          @click="emit('confirm')"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.profile {
  &.inline {
    display: flex;
    align-items: center;
    margin-left: 40px;
  }

  .profileImage {
    &.small {
      width: 96px;
      height: 96px;
    }

    &.large {
      width: 190px;
      height: 190px;
    }
  }
}
.select-el {
  bottom: 250px;
}
</style>
